import { z } from 'zod';
import {
  avatarSchema,
  bodySchema,
  facingSchema,
  memberTokenSchema,
  messageIdSchema,
  messageKindSchema,
  nicknameSchema,
  reactionEmojiSchema,
  roomNameSchema,
  seqSchema,
  userIdSchema,
} from './primitives.js';

/**
 * 클라이언트 → 서버 프레임.
 *
 * 판별자는 `t`. JSON으로 직렬화해 WebSocket으로 보낸다.
 */

/** 연결 직후 반드시 첫 프레임으로 보낸다. */
export const helloSchema = z.object({
  t: z.literal('hello'),
  /** 프로토콜 버전. 서버와 다르면 PROTOCOL_MISMATCH. */
  v: z.number().int().positive(),
  /** 재접속이면 이전에 받은 토큰. 최초 입장이면 생략. */
  memberToken: memberTokenSchema.optional(),
  nickname: nicknameSchema,
  avatar: avatarSchema,
  /**
   * 마지막으로 받은 메시지 seq.
   * 주면 서버가 그 이후 메시지만 보낸다(델타 동기화).
   * 없으면 최근 메시지 페이지를 보낸다.
   */
  sinceSeq: seqSchema.optional(),
});

export const sendSchema = z.object({
  t: z.literal('send'),
  /** 클라이언트 생성 UUID. 재전송돼도 서버가 중복을 걸러낸다. */
  id: messageIdSchema,
  kind: messageKindSchema.exclude(['system']),
  body: bodySchema,
  /** whisper면 발신 시점의 존/좌표 스냅샷. 서버가 가시 범위 판정에 쓴다. */
  zone: z.string().max(32).nullish(),
});

export const editSchema = z.object({
  t: z.literal('edit'),
  id: messageIdSchema,
  body: bodySchema,
});

export const deleteSchema = z.object({
  t: z.literal('delete'),
  id: messageIdSchema,
});

export const reactSchema = z.object({
  t: z.literal('react'),
  msgId: messageIdSchema,
  emoji: reactionEmojiSchema,
  /** true면 추가, false면 취소. */
  on: z.boolean(),
});

/**
 * 닉네임·아바타 변경.
 *
 * hello를 다시 보내지 않는다. 재접속은 델타 동기화를 동반하는 무거운
 * 동작인데, 아바타 색을 바꾸는 데 연결을 끊을 이유가 없다.
 */
export const profileSchema = z.object({
  t: z.literal('profile'),
  nickname: nicknameSchema.optional(),
  avatar: avatarSchema.optional(),
});

export const moveSchema = z.object({
  t: z.literal('move'),
  x: z.number(),
  y: z.number(),
  facing: facingSchema,
});

export const typingSchema = z.object({
  t: z.literal('typing'),
  on: z.boolean(),
});

/** 정수기·커피머신 같은 맵 오브젝트와 상호작용. */
export const interactSchema = z.object({
  t: z.literal('interact'),
  objectId: z.string().min(1).max(64),
});

export const pingSchema = z.object({
  t: z.literal('ping'),
});

/** owner 전용: 강퇴. */
export const kickSchema = z.object({
  t: z.literal('kick'),
  userId: userIdSchema,
});

/**
 * owner 전용: 방 설정 변경.
 *
 * 보낸 항목만 바뀐다. profile과 같은 모양이다 — 부분 갱신을 조립하기가
 * 쉽고, 나중에 항목이 늘어도 프레임을 새로 만들지 않아도 된다.
 */
export const roomUpdateSchema = z.object({
  t: z.literal('room'),
  name: roomNameSchema.optional(),
  /** true면 신규 입장이 막힌다. 이미 들어와 있던 사람은 계속 다닌다. */
  locked: z.boolean().optional(),
});

/**
 * owner 전용: 초대코드 회전.
 *
 * room 프레임의 불리언으로 넣지 않는다. 되돌릴 수 없는 동작이라 실수로
 * 함께 보내지는 일이 없어야 하고, 부분 갱신과 성격이 다르다.
 */
export const rotateCodeSchema = z.object({
  t: z.literal('rotateCode'),
});

export const clientMessageSchema = z.discriminatedUnion('t', [
  helloSchema,
  sendSchema,
  editSchema,
  deleteSchema,
  reactSchema,
  moveSchema,
  typingSchema,
  interactSchema,
  pingSchema,
  kickSchema,
  profileSchema,
  roomUpdateSchema,
  rotateCodeSchema,
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ClientMessageType = ClientMessage['t'];

/** 특정 타입만 좁혀 쓰고 싶을 때. 예: `Extract<ClientMessage, { t: 'send' }>` */
export type HelloMessage = z.infer<typeof helloSchema>;
export type SendMessage = z.infer<typeof sendSchema>;
export type MoveMessage = z.infer<typeof moveSchema>;
